/* eslint-disable import/prefer-default-export */
import get from 'lodash/get';
import { getSelectedVendorId } from 'shared/helpers/multiSeller';
import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';

// types
import { IDetailState, IMedia } from 'shared/store/details/types';
import {
  IProductInfo,
  IProductInfoList,
} from 'shared/layouts/DetailsPage/components/ProductInfo/Mobile/components/ReturnAndExchange/types';

// constants
import { RETURN_AND_EXCHANGE_SLUG } from 'shared/layouts/DetailsPage/components/ProductInfo/constants';
import { LUXE_CATALOG_TAG } from 'shared/constants/storeConfig';
// helpers
import { transformDeliveryText, transformDeliveryTextV1 } from 'shared/store/transformer/transform';
import { minDeliveryOrShippingDay, minDeliveryOrShippingDayV1 } from './helpers';
import { getValidCouponsData } from 'shared/helpers/api/helpers';
import {
  getValidFaqItems,
  isKnowYourProductWidget,
} from 'shared/layouts/DetailsPage/components/ProductInfo/components/ProductDescription/components/ProductAeoFaq/helpers';
import { WIDGET_TYPES } from 'shared/layouts/DetailsPage/components/ProductInfo/components/ProductDescription/constants';

export const details = (state: { details: IDetailState; }): IDetailState => state.details;

export const getSKUData = createSelector([details], (detailData) => get(detailData, 'skuData'));

export const getProductData = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product', {})
);

export const getProductTitle = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.title', '')
);

export const getProductSubTitle = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.subTitle', '')
);

export const getProductImages = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.productMedia', [])
);

export const getProductImagesAspectRatio = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.productMedia[0].aspect_ratio', 0.75)
);

export const getErrorSKU = createSelector([details], (detailData) =>
  get(detailData, 'errorSKU', '')
);

export const getErrorStatusCode = createSelector([details], (detailData) =>
  get(detailData, 'errorStatusCode', '')
);

export const getProductUrl = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.action_url', '')
);

export const getProductId = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.id', '')
);

export const getProductSKU = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.sku', '')
);

export const getProductOutOfStock = createSelector([details], (detailData) =>
  Boolean(get(detailData, 'skuData.product.isOutOfStock', false))
);

export const getProductBrandUrl = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.brandActionUrl', '')
);

export const getProductPrice = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.price', '')
);

export const getProductDiscount = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.discount', '')
);

export const getProductDiscountPrice = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.discountedPrice', '')
);

export const getProductSizeOptions = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.sizeOptions', {})
);

export const getProductInventoryNudge = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.inventoryNudge', '')
);

export const isOneSize = createSelector([details], (detailData) =>
  Boolean(get(detailData, 'skuData.product.isOneSize', false))
);

export const isReturnable = createSelector([details], (detailData) =>
  Boolean(get(detailData, 'skuData.product.isReturnable', true))
);

export const getLowReturnNudge = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.low_return_nudge', null)
);

export const getHeroImageData = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.aPlusContent', '')
);

export const getHeroImageV3 = createSelector([details], (detailData) => {
  const pdp_sections = get(detailData, 'skuData.product.pdp_sections', []);
  const heroImageData = pdp_sections.filter((widget: any) => widget.widget_type === 'aplus_widget');

  if (heroImageData[0]) {
    return heroImageData[0].hero_image;
  }
});

export const getHeroImageDataAPIError = createSelector([details], (detailData) =>
  get(detailData, 'isFetchingAplusDataError', false)
);

export const getAPlusContentAPIStatus = createSelector([details], (detailData) =>
  get(detailData, 'isAPlusContentFetched', false)
);

export const getAplusErrorStatusCode = createSelector([details], (detailData) =>
  get(detailData, 'aplusErrorStatusCode', '')
);

export const isFetching = createSelector([details], (detailData) =>
  get(detailData, 'isFetching', false)
);

export const isFetchingError = createSelector([details], (detailData) =>
  get(detailData, 'isFetchingError', false)
);

export const getFetchingSKU = createSelector([details], (detailData) =>
  get(detailData, 'fetchingSKU', '')
);

export const getProductParentSKU = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.parentSku', '')
);

export const isNotFound = createSelector([details], (detailData) =>
  get(detailData, 'isNotFound', false)
);

export const getProductTag = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.tag.title', '')
);

export const getProductDesigner = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.aboutDesigner')
);

export const getBrandBook = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.brand_book', [])
);

export const getProductMetaData = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.meta_data', {})
);

export const getExploreProductID = createSelector([details], (detailData) =>
  get(detailData, 'exploreProductID', '')
);

export const getBadge = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.badge', {})
);

export const getIsLuxe = createSelector([getProductData], (productData) => {
  const rawTags = get(productData, 'catalog_tag', []);
  const catalogTags: string[] = Array.isArray(rawTags) ? rawTags : rawTags.split(',');
  return catalogTags.includes(LUXE_CATALOG_TAG);
});

export const getProductCategoryIds = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.categoryId', [])
);

export const getSizeGuideURL = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.sizeGuideURL', '')
);

export const getReviewSummary = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.review_rating_json', {})
);

export const getAllReviewImages = createSelector([details], (detailData) =>
  get(detailData, 'customerReviewsImages.reviewImages', [])
);

export const getLastImageFetchedKey = createSelector([details], (detailData) =>
  get(detailData, 'customerReviewsImages.pageKey', '')
);

export const isReviewImagesFetching = createSelector([details], (detailData) =>
  get(detailData, 'customerReviewsImages.isFetching', true)
);

export const isReviewImagesFetchingError = createSelector([details], (detailData) =>
  get(detailData, 'customerReviewsImages.isError', false)
);

export const getImageRelatedReviewData = createSelector([details], (detailData) =>
  get(detailData, 'customerReviewsImages.reviewData', {})
);

export const getTotalReviewImages = createSelector([details], (detailData) =>
  get(detailData, 'customerReviewsImages.total', false)
);

export const getAllReviews = createSelector([details], (detailData) =>
  get(detailData, 'reviews.data', [])
);

export const isReviewFetching = createSelector([details], (detailData) =>
  get(detailData, 'reviews.isFetching', false)
);

export const allReviewPageNumber = createSelector([details], (detailData) =>
  get(detailData, 'reviews.page', 0)
);

export const isReviewError = createSelector([details], (detailData) =>
  get(detailData, 'reviews.isError', false)
);

export const getDesignCode = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.design_code', '')
);

export const getReturnPolicyInfo = createSelector([details], (detailData) => {
  const productInfo: IProductInfoList = get(detailData, 'skuData.product.productInfo', []);
  const returnAndExchangeDataInArray = productInfo.filter((info: IProductInfo) => {
    return info.slug === RETURN_AND_EXCHANGE_SLUG;
  });
  return returnAndExchangeDataInArray[0];
});

export const isVideoAvailableOnPDP = createSelector([details], (detailData) => {
  let isVideoAvailable = false;
  let medias = get(detailData, 'skuData.product.productMedia', []);

  medias.forEach((media: IMedia) => {
    if (media.mediaType === 'video') {
      isVideoAvailable = true;
    }
  });
  return isVideoAvailable;
});

export const getColorOptions = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.colorOptions', [])
);

export const getDefaultColor = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.color.name', '')
);

export const getTagTitles = createSelector([details], (detailData) =>
  get(detailData, 'selectedSKU.tag_list', get(detailData, 'skuData.product.tag_list', [])).map(
    (tag: { title: string; }) => tag.title
  )
);

export const getDisclaimerText = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.disclaimer', '')
);

export const getCashOnDeliveryDetails = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.cash_on_delivery', '')
);

export const getNewEcomPolicies = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.ecom_policies', '')
);

export const getDeliveryDetails = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.deliveryDetails', '')
);

export const getDeliveryInfo = createSelector([details], (detailData) =>
  get(detailData, 'ecomPolicyData.deliveryInfo', {})
);

export const getReturnExchangeDetails = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.return_and_exchanges', '')
);

export const getSelectedSKUID = createSelector([details], (detailData) =>
  get(detailData, 'selectedSKUId', undefined)
);

export const getSelectedSKUVendorId = createSelector([details], (detailData) =>
  getSelectedVendorId(detailData.selectedSKU, detailData.skuData?.product?.vendor_id)
);

export const getSelectedSKUVendorType = createSelector([details], (detailData) =>
  get(detailData, 'selectedSKU.vendor_type', get(detailData, 'skuData.product.vendor_type', ''))
);

export const getEcomPolicyData = createSelector([details], (detailData) =>
  get(detailData, 'ecomPolicyData')
);

export const isEComAPIError = createSelector([details], (detailData) =>
  get(detailData, 'isFetchingEcomPolicyError')
);

export const isEComAPIFetching = createSelector([details], (detailData) =>
  get(detailData, 'isFetchingEcomPolicyProgress')
);

export const getModelWidgetData = createSelector([details], (detailData) => {
  return get(detailData, 'skuData.product.model_details', {});
});

export const getResponsibleTags = createSelector([details], (detailData) => {
  const pdp_sections = get(detailData, 'skuData.product.pdp_sections', []);
  const tags = pdp_sections.filter((widget: any) => widget.widget_type === 'responsible_widget');

  return tags.length > 0 ? tags[0] : {};
});

export const getReponsibleTagsAvailable = createSelector([details], (detailData) => {
  const pdp_sections = get(detailData, 'skuData.product.pdp_sections', []);
  const tags = pdp_sections.filter((widget: any) => widget.widget_type === 'responsible_widget');

  return tags.length > 0;
});

export const getAllInStockSKUs = createSelector([details], (detailData) => {
  const SKUs: string[] = [];
  const sizes = get(detailData, 'skuData.product.sizeOptions.options', []);

  // if product has only 1 size
  if (sizes.length < 1) {
    const parentSKU = get(detailData, 'skuData.product.sku', '');
    return [parentSKU];
  }

  // add only thoses skus which are not out of stock
  for (const size of sizes) {
    if (size.isOutOfStock === 0) {
      SKUs.push(size.sku);
    }
  }

  return SKUs;
});

export const getDeliveryOrShippingText = createSelector([details], (detailData) => {
  const parentSKU = get(detailData, 'skuData.product.sku', '');
  const selectedSKU = get(detailData, 'selectedSKU.sku', '');
  const noOfSizes = get(detailData, 'skuData.product.sizeOptions.options.length', 0);
  const freeDeliveryAvailable = get(detailData, 'skuData.product.deliveryDetails.is_free_delivery');

  // serviceability data for skus which are not out of stock
  const data = get(detailData, 'skuData.product.deliveryDetails.deliveryDataForSKUs.details', {});

  const eddResponse = get(detailData, 'skuData.product.deliveryDetails.deliveryDataForSKUs', {});

  const parentSkuEDDMessage = get(eddResponse, 'message', '');

  const skuData = get(eddResponse, 'details', {});

  if (selectedSKU !== '') {
    return get(skuData, `${[selectedSKU]}.message`, parentSkuEDDMessage);
  } else if (parentSkuEDDMessage !== '') {
    return parentSkuEDDMessage;
  }

  // if no of sizes is 0 means we have product with one size
  if (noOfSizes === 0) {
    return transformDeliveryText(data, parentSKU, freeDeliveryAvailable);
  }

  // we have product with different sizes and size is selected
  if (selectedSKU !== '') {
    return transformDeliveryText(data, selectedSKU, freeDeliveryAvailable);
  }

  // we have product with different sizes and size is not selected by user
  // so return the date which is closest to today for delivery or shipping
  return minDeliveryOrShippingDay(data, freeDeliveryAvailable);
});

export const hasViewSimilar = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.has_visual_similarity_recos', false)
);

export const getSelectedSKUDataPDPSections = createSelector([details], (detailData) => {
  const parentSKU = get(detailData, 'skuData.product.pdp_sections', []);
  const selectedSKU = get(detailData, 'selectedSKU.pdp_sections', []);
  return selectedSKU.length > 0 ? selectedSKU : parentSKU;
});

export const getSelectedSKUFaq = createSelector([getSelectedSKUDataPDPSections], (sections) => {
  const kypWidget = (sections || []).find(
    (widget: {
      widget_id?: string;
      title?: string;
      widget_type?: string;
      faq?: Array<{ que: string; ans: string }>;
    }) =>
      isKnowYourProductWidget(widget) &&
      widget?.widget_type === WIDGET_TYPES.plainCollapsableWidget
  );
  return getValidFaqItems(kypWidget?.faq);
});

export const getCategoryLevel = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.primary_categories_json_all', {})
);

export const getSelectedSizeName = createSelector([details], (detailData) =>
  get(detailData, 'selectedSKU.sizeName', '')
);

export const getTags = createSelector([details], (detailData) =>
  get(detailData, 'selectedSKU.tag_list', get(detailData, 'skuData.product.tag_list', []))
);

export const getIsProductOffersAvailable = createSelector(
  [details],
  (detailData) => get(detailData, 'productOffers.offers', []).length
);

export const getProductOffers = createSelector([details], (detailData) =>
  get(detailData, 'productOffers', { coupons: [], offers: [], fetching: false, bestOffers: [], onSale: false, specialTag: {} })
);

export const getValidProductOffers = createSelector([details], (detailData) => {
  const { coupons, offers } = get(detailData, 'productOffers', {
    coupons: [],
    offers: [],
    fetching: false,
  });

  // filter expired coupons
  return { coupons: getValidCouponsData(coupons), offers: getValidCouponsData(offers) };
});

export const getIsOffersFetching = createSelector([details], (detailData) =>
  get(detailData, 'productOffers.fetching', false)
);

export const getUrgencyNudge = createSelector([details], (detailData) => {
  const parentUrgencyTag = get(detailData, 'skuData.product.urgencyTag', '');
  const selectedSKU = get(detailData, 'selectedSKU.sku', '');
  const selectedUrgencyTag = get(detailData, 'selectedSKU.urgencyTag', '');
  const tagIndentifier = get(
    detailData,
    selectedSKU ? 'selectedSKU.urgencyTagIdentifier' : 'skuData.breadcrumbs.urgencyTagIdentifier',
    ''
  );
  return selectedSKU
    ? { tag: selectedUrgencyTag, tagIndentifier }
    : { tag: parentUrgencyTag, tagIndentifier };
});

export const getFomoTag = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.fomo_tag', '')
);

export const getBreadCrumbs = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.breadcrumbs', [])
);

export const getEDDText = createSelector([details], (detailData) => {
  const selectedSKU = get(detailData, 'selectedSKU.sku', '');

  const eddResponse = get(detailData, 'skuData.product.deliveryDetails.eddDetails', {});

  const parentSkuEDDMessage = get(eddResponse, 'message', '');

  const skuData = get(eddResponse, 'details', {});

  if (selectedSKU !== '') {
    return get(skuData, `${[selectedSKU]}.message`, parentSkuEDDMessage);
  }
  return parentSkuEDDMessage;
});

export const getSizeGuideData = createSelector([details], (detailData) => {
  return get(detailData, 'skuData.product.sizeGuideData', {});
});

export const getProductImage = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.imageUrl', 'false')
);

export const getIsSizeGuideDataAvailable = createSelector([details], (detailData) => {
  return isEmpty(get(detailData, 'skuData.product.sizeOptions.meta', {})) === false;
});

export const getSpecialTagsInfo = createSelector([details], (detailData) => {
  const specialTag = get(detailData, 'skuData.product.specialTag', null);

  return {
    isSpecialTagsPresent: !!specialTag,
    specialTagsName: specialTag?.type || '',
    specialTag: specialTag
  };
});

export const isSaleParticipating = createSelector([details], (detailData) => {
  return get(detailData, 'skuData.product.is_sale_participating', false);
});

export const getDeliveryOrShippingTextV1 = createSelector([details], (detailData) => {
  const parentSKU = get(detailData, 'skuData.product.sku', '');
  const selectedSKU = get(detailData, 'selectedSKU.sku', '');
  const noOfSizes = get(detailData, 'skuData.product.sizeOptions.options.length', 0);
  const freeDeliveryAvailable = get(detailData, 'skuData.product.deliveryDetails.is_free_delivery');

  // serviceability data for skus which are not out of stock
  const data = get(detailData, 'skuData.product.deliveryDetails.deliveryDataForSKUs', {});

  // if no of sizes is 0 means we have product with one size
  if (noOfSizes === 0) {
    return transformDeliveryTextV1(data, parentSKU, freeDeliveryAvailable);
  }

  // we have product with different sizes and size is selected
  if (selectedSKU !== '') {
    return transformDeliveryTextV1(data, selectedSKU, freeDeliveryAvailable);
  }

  // we have product with different sizes and size is not selected by user
  // so return the date which is closest to today for delivery or shipping
  return minDeliveryOrShippingDayV1(data, freeDeliveryAvailable);
});

export const getBrandLaunchMetadata = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.brand_launch_metadata', {})
);

export const getBrandLaunchingSoon = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.brand_launching_soon', false)
);

export const getProductCatalogTag = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.catalog_tag', []) as string[]
);

export const getLuxeActionUrl = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.luxe_action_url', '') as string
);

export const getStorePriority = createSelector([details], (detailData) =>
  get(detailData, 'skuData.product.store_priority', []) as string[]
);
